/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable @typescript-eslint/no-shadow */

import { useCallback, useEffect, useState } from "react"
import { debounce, includes, uniqBy } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import {
  FILTER_LIMIT,
  SEARCH_LIMIT,
  STORE_LIST_LIMIT,
} from "components/search/searchBarConstants"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, FilterTrigger } from "constants/tracking"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { MerchantStoreInfo, StoreFilter } from "types/models/menu"
import { actionCreator } from "store/duck/menu"
import { MerchantAggregate } from "types/api"

type FilterTrackerData = { trigger: FilterTrigger; category: EventCategory }

const ALL = "ALL"

const isFilterCityActive = (storeFilter: StoreFilter) => {
  return !!storeFilter?.city?.length && !storeFilter?.city?.includes(ALL)
}

const useMenuStoreList = (
  outletsOverride?: MerchantStoreInfo[],
  includeMenuGroupV2ID?: boolean,
) => {
  const dispatch = useDispatch()

  const [inputValue, setInputValue] = useState<string>("")

  const [filter, setFilter] = useState<StoreFilter>({
    search: "",
    city: [ALL],
    asc: undefined,
  })

  const {
    menu: {
      outletsSearchHasMore,
      outletsSearch = [],
      isFetchingMoreOutlets,
      isFetchingOutletsSearch,
      cityList,
    },
  } = useSelector((state: AppState) => state)

  const getCityListOption = () => {
    if (outletsOverride)
      return uniqBy(
        outletsOverride?.map(({ city }) => ({
          text: city,
          value: city,
        })),
        "value",
      )

    return [
      ...(cityList || [])?.map(({ cityName, cityID }) => ({
        text: cityName,
        value: `${cityID}`,
      })),
    ]
  }

  const filterMerchantByCity = (merchant: MerchantStoreInfo): boolean => {
    if (!isFilterCityActive(filter)) return true

    const cityNames = filter?.city.map(
      item =>
        (cityList || []).find(city => city.cityID.toString() === item)
          ?.cityName,
    )

    return cityNames.includes(merchant.city)
  }

  const filterByCityAndSearch = (merchant: MerchantStoreInfo) => {
    return (
      filterMerchantByCity(merchant) &&
      includes(
        merchant.merchantName?.toLowerCase(),
        filter?.search?.toLowerCase(),
      )
    )
  }

  const showLoadMore = outletsSearchHasMore && !isFetchingOutletsSearch

  const filteredList = (() => {
    if (outletsOverride)
      return uniqBy(outletsOverride.filter(filterByCityAndSearch), "merchantID")
    if (isFetchingOutletsSearch || filter?.search?.length > SEARCH_LIMIT)
      return []
    return outletsSearch
  })()

  const handleFilter = useCallback(
    async (
      filterObj: StoreFilter,
      { trigger, category }: FilterTrackerData,
    ) => {
      setFilter(filterObj)

      const body = {
        ...filterObj,
        search: filterObj.search.trim(),
        limit: filterObj?.search?.length ? FILTER_LIMIT : STORE_LIST_LIMIT,
        includeItemsWithoutPhotosCount: true,
        includeMenuGroupV2ID,
      }

      const res: any = await dispatch(actionCreator.searchOutlets(body))
      const merchantStore: MerchantStoreInfo[] =
        res?.payload?.data?.merchants || []

      TroyWebTracker.sendEvent({
        category,
        action: EventAction.searchStore,
        labels: {
          FILTER_TEXT: filterObj.search,
          FILTER_ASC: filterObj.asc ?? true,
          FILTER_CITY: filterObj.city,
          TRIGGER: trigger,
          RESULT: merchantStore.map(item => item.merchantName).toString(),
          TOTAL_RESULT: merchantStore.length,
        },
      })
    },
    [dispatch, includeMenuGroupV2ID],
  )

  const debouncedSearch = useCallback(
    debounce((filter: StoreFilter, trackParam: FilterTrackerData) => {
      handleFilter(filter, trackParam)
    }, DEFAULT_DEBOUNCE_WAIT_TIME),
    [handleFilter],
  )

  const handleSearchFilter = (val: string, trackParam: FilterTrackerData) => {
    setInputValue(val)
    debouncedSearch({ ...filter, search: val.trim() }, trackParam)
  }

  const handleLoadMoreOutlets = () => {
    if (isFetchingMoreOutlets) return

    const body = {
      ...filter,
      search: filter.search.trim(),
      offset: outletsSearch.length,
      limit: filter.search.length ? FILTER_LIMIT : STORE_LIST_LIMIT,
      includeItemsWithoutPhotosCount: true,
      isLoadMore: true,
      includeMenuGroupV2ID,
    }

    dispatch(actionCreator.searchOutlets(body))
    TroyWebTracker.sendEvent({
      category: EventCategory.menuHomePage,
      action: EventAction.loadNextPage,
    })
  }

  useEffect(() => {
    dispatch(
      actionCreator.fetchMerchantAggregate(
        MerchantAggregate.cityID,
        MerchantAggregate.businessType,
      ),
    )

    dispatch(actionCreator.fetchItemsWithoutPhotoCount())
  }, [dispatch])

  return {
    handleSearchFilter,
    handleFilter,
    filter,
    filteredList,
    handleLoadMoreOutlets,
    showLoadMore,
    inputValue,
    cityList: getCityListOption(),
  }
}

export default useMenuStoreList
